import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/hizmetler/marka-stratejisi';
const pageUrl = `${siteUrl}${pagePath}`;

const brandFAQItems = [
  {
    question: 'Marka stratejisi nedir?',
    answer:
      'Marka stratejisi, markanızın pazarda nasıl konumlanacağını, hangi kitleye ne söyleyeceğini ve rakiplerden nasıl ayrışacağını belirleyen bütüncül plandır. Logo, renk ve font seçiminden çok daha önce başlar.',
  },
  {
    question: 'Marka stratejisi ile görsel kimlik arasındaki fark nedir?',
    answer:
      'Görsel kimlik (logo, renk, tipografi) marka stratejisinin görünen yüzüdür. Strateji ise markanın hangi boşluğu doldurduğu, kime hitap ettiği, nasıl bir tonla konuştuğu ve rakiplerden nasıl farklılaştığıdır. Önce strateji gelir, görsel kimlik onu takip eder.',
  },
  {
    question: 'Marka konumlandırması nasıl yapılır?',
    answer:
      'Pazar araştırması, hedef kitle segmentasyonu, rakip analizi ve markanın içsel yetkinliklerinin kesişiminde benzersiz bir konum belirlenir. Bu konum, markanın tüm iletişimine yön verir.',
  },
  {
    question: 'Rebranding ne zaman gerekir?',
    answer:
      'Pazar değiştiğinde, hedef kitle evrildiğinde, marka büyüdüğünde veya mevcut konumlandırma artık markayı temsil etmediğinde rebranding gerekir. Rebranding sadece logo değiştirmek değildir; stratejik bir yeniden konumlanmadır.',
  },
  {
    question: 'Marka stratejisi ne kadar sürer?',
    answer:
      'Kapsamlı bir marka stratejisi projesi genelde 4-8 hafta arasında tamamlanır. Araştırma, strateji geliştirme, yaratıcı uygulama ve lansman planlaması aşamalarını içerir.',
  },
  {
    question: 'Küçük işletmeler için marka stratejisi gerekli mi?',
    answer:
      'Evet. Özellikle küçük işletmeler için marka stratejisi, sınırlı bütçeyle maksimum etki yaratmanın anahtarıdır. Doğru konumlandırma olmadan yapılan her pazarlama yatırımı verimsizdir.',
  },
];

const faqSchema = getFAQSchema(brandFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'Marka Stratejisi & Branding Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'Marka stratejisi, konumlandırma, Brand DNA, mesaj ve görsel kimlik hizmetleriyle markanızı pazarda doğru konumlandırıyoruz. BIFF Digital marka stratejisini araştırma ve veriyle birlikte yönetir.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'Marka Stratejisi & Branding',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Marka Stratejisi Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pazar Araştırması & Rakip Analizi', description: 'Sektörünüzün ve rakiplerinizin derinlemesine analizi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Brand DNA & Positioning', description: 'Marka kimliği, konumlandırma ve farklılaşma stratejisi' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Messaging & Tone of Voice', description: 'Marka mesaj mimarisi ve iletişim tonu' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Visual Identity', description: 'Görsel kimlik tasarımı ve marka rehberi' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${siteUrl}/hizmetler` },
        { '@type': 'ListItem', position: 3, name: 'Marka Stratejisi & Branding', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function MarkaStratejisi() {
  useEffect(() => {
    document.title = 'Marka Stratejisi & Branding Ajansı | Konumlandırma | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="section-padding pb-0 md:pb-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">HİZMETLER</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-8">
              Marka<br />Stratejisi
            </h1>
            <div className="h-px w-full bg-gradient-to-r from-white/15 via-white/5 to-transparent mb-10" />
            <p className="text-xl md:text-2xl text-foreground-200 font-medium leading-relaxed mb-6">
              Logo Değil.<br />
              <span className="text-primary-500">Konumlandırma.</span>
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-2xl speakable-summary">
              Herkes marka ister. Kimse konumlandırma yapmaz. Logo yaptırmakla marka olunmaz. Marka, pazarda bir boşluğu doldurduğunuzda, bir anlam ifade ettiğinizde ve bunu tutarlı şekilde ilettiğinizde oluşur.
            </p>
          </div>
        </section>

        {/* Herkes Marka İster */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Herkes Marka İster.<br />Kimse Konumlandırma Yapmaz.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Bir logo, bir renk paleti, bir web sitesi — bunlar markanın görünen kısmıdır. Ama marka aslında insanların zihninde kapladığınız yerdir. O yeri inşa etmeden yapılan her tasarım, her reklam, her içerik boşa gider.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed">
                Markanın söylediğiyle insanların anladığı aynı mı?
              </p>
              <p className="text-sm text-foreground-300 mt-3 leading-relaxed">
                İşte strateji tam olarak bu soruyla başlar.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding bg-background-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">NE YAPIYORUZ?</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Stratejiden Görsel Kimliğe<br />Bütüncül Marka İnşası
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-search-line', title: 'Pazar Araştırması', desc: 'Sektörünüzün dinamiklerini, büyüme alanlarını ve fırsat boşluklarını analiz ediyoruz. Markanızın nerede duracağına veriyle karar veriyoruz.' },
                { icon: 'ri-scales-3-line', title: 'Rakip Analizi', desc: 'Rakiplerinizi sadece ne yaptıklarıyla değil, neyi yapmadıklarıyla da okuyoruz. Farklılaşma alanlarını tam olarak bu boşluklarda buluyoruz.' },
                { icon: 'ri-dna-line', title: 'Brand DNA', desc: 'Markanızın özünü tanımlıyoruz: amaç, vizyon, misyon, değerler, kişilik. Tüm iletişiminizin temelini oluşturacak marka kodunu yazıyoruz.' },
                { icon: 'ri-focus-3-line', title: 'Positioning', desc: 'Pazardaki benzersiz yerinizi belirliyoruz. Hangi kitleye, hangi ihtiyaca, hangi farklılaşmayla hitap edeceksiniz — net ve ölçülebilir şekilde tanımlıyoruz.' },
                { icon: 'ri-message-3-line', title: 'Messaging & Tone of Voice', desc: 'Markanızın nasıl konuşacağını, hangi kelimeleri kullanacağını ve hangi duyguyu uyandıracağını belirliyoruz. Tutarlı bir iletişim tonu inşa ediyoruz.' },
                { icon: 'ri-palette-line', title: 'Visual Identity', desc: 'Stratejiyi görsele taşıyoruz. Logo, renk paleti, tipografi, görsel dil ve marka rehberi — hepsi stratejiden beslenen, tutarlı bir sistem olarak çıkıyor.' },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Metodoloji */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              Nasıl Çalışıyoruz?
            </h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Keşif', desc: 'İşinizi, sektörünüzü, hedef kitlenizi ve rakiplerinizi anlamak için derinlemesine bir keşif süreci yürütüyoruz. Stakeholder görüşmeleri, müşteri içgörüleri ve pazar verilerini bir araya getiriyoruz.' },
                { step: '02', title: 'Strateji', desc: 'Toplanan tüm veriyi Brand DNA, positioning, messaging ve tone of voice çerçevesinde yapılandırıyoruz. Strateji belgesi, markanın pusulası oluyor.' },
                { step: '03', title: 'Yaratıcı Uygulama', desc: 'Stratejiyi görsel ve sözel dile çeviriyoruz. Logo, kimlik, mesaj mimarisi, içerik stratejisi — hepsi aynı stratejik temelden besleniyor.' },
                { step: '04', title: 'Lansman ve Yaşatma', desc: 'Yeni marka kimliğini pazara sunuyor, iç ve dış iletişimi yönetiyor, markanın tutarlı şekilde yaşaması için rehber ve eğitim sağlıyoruz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={brandFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="MARKA STRATEJİSİ"
          sectionSummary="Marka stratejisi ve konumlandırma hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Markanızın ne söylediğini değil,<br />
                  <span className="text-primary-500">pazarda ne anlama geldiğini</span> konuşalım.
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Ücretsiz marka analizi görüşmesinde mevcut konumunuzu değerlendirelim. Markanız bugün pazarda gerçekten ne ifade ediyor?
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Marka Analizi Planla
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}